import { Pencil, MousePointer, Trash2, Undo2, Redo2, Move, CircleDot, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { SketchMode, PipeSize, PipeMaterial, FittingType } from "@/hooks/use-sketch-state";

const pipeSizes: PipeSize[] = ['1/2"', '3/4"', '1"', '1-1/4"', '1-1/2"', '2"', '2-1/2"', '3"', '4"', '6"', '8"', '10"', '12"'];
const materials: PipeMaterial[] = ["Copper", "Steel", "PVC", "CPVC", "Stainless", "Cast Iron"];
const fittingTypes: { value: FittingType; label: string }[] = [
  { value: "elbow90", label: "90° Elbow" },
  { value: "elbow45", label: "45° Elbow" },
  { value: "tee", label: "Tee" },
  { value: "reducer", label: "Reducer" },
  { value: "coupling", label: "Coupling" },
  { value: "valve", label: "Valve" },
  { value: "cap", label: "Cap" },
];

const modeButtons: { mode: SketchMode; icon: typeof Pencil; label: string }[] = [
  { mode: "draw", icon: Pencil, label: "Draw" },
  { mode: "fitting", icon: CircleDot, label: "Fitting" },
  { mode: "select", icon: MousePointer, label: "Select" },
  { mode: "delete", icon: Trash2, label: "Delete" },
  { mode: "pan", icon: Move, label: "Pan" },
];

interface Props {
  mode: SketchMode;
  setMode: (m: SketchMode) => void;
  activeSize: PipeSize;
  setActiveSize: (s: PipeSize) => void;
  activeMaterial: PipeMaterial;
  setActiveMaterial: (m: PipeMaterial) => void;
  activeFitting: FittingType;
  setActiveFitting: (f: FittingType) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  clearAll: () => void;
}

const SketchToolbar = ({
  mode, setMode, activeSize, setActiveSize, activeMaterial, setActiveMaterial,
  activeFitting, setActiveFitting, undo, redo, canUndo, canRedo, clearAll,
}: Props) => (
  <div className="flex flex-wrap items-center gap-2 p-3 bg-card border border-border rounded-xl">
    {/* Mode buttons */}
    <div className="flex items-center gap-1 border-r border-border pr-2">
      {modeButtons.map(({ mode: m, icon: Icon, label }) => (
        <Button
          key={m}
          size="sm"
          variant={mode === m ? "default" : "outline"}
          onClick={() => setMode(m)}
          title={label}
          className="h-8 w-8 p-0"
        >
          <Icon className="w-4 h-4" />
        </Button>
      ))}
    </div>

    {/* Pipe size */}
    <Select value={activeSize} onValueChange={v => setActiveSize(v as PipeSize)}>
      <SelectTrigger className="w-20 h-8 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {pipeSizes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
      </SelectContent>
    </Select>

    {/* Material */}
    <Select value={activeMaterial} onValueChange={v => setActiveMaterial(v as PipeMaterial)}>
      <SelectTrigger className="w-24 h-8 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {materials.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
      </SelectContent>
    </Select>

    {/* Fitting type (visible only in fitting mode) */}
    {mode === "fitting" && (
      <Select value={activeFitting} onValueChange={v => setActiveFitting(v as FittingType)}>
        <SelectTrigger className="w-28 h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {fittingTypes.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
        </SelectContent>
      </Select>
    )}

    {/* Undo / Redo / Clear */}
    <div className="flex items-center gap-1 ml-auto border-l border-border pl-2">
      <Button size="sm" variant="ghost" onClick={undo} disabled={!canUndo} title="Undo" className="h-8 w-8 p-0">
        <Undo2 className="w-4 h-4" />
      </Button>
      <Button size="sm" variant="ghost" onClick={redo} disabled={!canRedo} title="Redo" className="h-8 w-8 p-0">
        <Redo2 className="w-4 h-4" />
      </Button>
      <Button size="sm" variant="ghost" onClick={clearAll} title="Clear All" className="h-8 w-8 p-0 text-destructive">
        <RotateCcw className="w-4 h-4" />
      </Button>
    </div>
  </div>
);

export default SketchToolbar;
